import { Box, Link, Typography, Alert, Tabs, Tab } from '@mui/material';
import { useEffect, useState } from 'react';
import { useSearchParams } from 'react-router-dom';
import { DriveFileAccessApproval } from './DriveFileAccessApproval';
import { DataGrid, GridColDef, GridRenderCellParams } from '@mui/x-data-grid';
import { useAuthStore } from 'src/stores/AuthStore/AuthStore';
import { observer } from 'mobx-react-lite';
import {
  FILE_PICK_SUCCESS_MESSAGE,
  SYNC_FILE_PICK_STARTED,
} from 'src/constants/dialogMetaInfo';
import { useAlertStore } from 'src/stores/AlertStore/AlertStore';
import {
  UNHANDLED_ERROR,
  USER_DETAILS_NOT_FOUND_ERROR,
} from 'src/constants/errors';
import { isString } from 'formik';
import { COPY_NOTIFICATION_MESSAGE } from 'src/constants/notification';
import TablePaginationActions from 'src/constants/TablePaginationActions';
import { ModuleHeader } from 'src/components/module-header/ModuleHeader';
import GooglePickerComponent, {
  FileData,
} from 'src/components/google-picker/googleFilePicker';
import {
  PAGE_SIZE_OPTIONS,
  WORDS_PER_ROW_CELL,
  truncateString,
} from 'src/constants/utils';
import { MODULE_USAGE_DESCRIPTION } from 'src/constants/toolTip';
import { SearchBar } from 'src/components/search-bar/SearchBar';
import {
  createFilePick,
  getFilePicks,
  FilePickPayload,
  updateFilePickStatus,
} from 'src/services/filePicks';
import { Button } from '@mui/material';
import { FILE_PICK_STATUS } from 'src/constants/filePick';
import InfoDrawerTrigger from 'src/components/info-drawer-trigger/InfoDrawerTrigger';
import {
  FILE_PICKER_ACCESS_INFO_MARKDOWN,
  FILE_PICKER_ACCESS_INFO_TITLE,
  FILE_PICKER_MODULE_INFO_MARKDOWN,
  FILE_PICKER_MODULE_INFO_TITLE,
} from 'src/constants/moduleHelp';

interface FileRow {
  id: string;
  file_id: string;
  name: string;
  url: string;
  uploaded_on: string;
  status: FILE_PICK_STATUS;
}

const FilePickerInventory = observer(() => {
  const { getUserDetails } = useAuthStore();
  const userDetails = getUserDetails();
  const alertStore = useAlertStore();
  const { notify } = alertStore;

  const [tabValue, setTabValue] = useState(0);

  const [loadingSelf, setLoadingSelf] = useState(false);
  const [loadingOrg, setLoadingOrg] = useState(false);

  const [rowsSelf, setRowsSelf] = useState<FileRow[]>([]);
  const [rowsOrg, setRowsOrg] = useState<FileRow[]>([]);

  const [rowCountSelf, setRowCountSelf] = useState(0);
  const [rowCountOrg, setRowCountOrg] = useState(0);

  const [paginationSelf, setPaginationSelf] = useState({
    page: 1,
    pageSize: PAGE_SIZE_OPTIONS[0],
  });

  const [paginationOrg, setPaginationOrg] = useState({
    page: 1,
    pageSize: PAGE_SIZE_OPTIONS[0],
  });

  const [searchText, setSearchText] = useState<string>('');

  const isAdmin = userDetails?.role === 'ADMIN';

  const columns: GridColDef<FileRow>[] = [
    {
      field: 'name',
      headerName: 'Name',
      flex: 3,
      renderCell: ({ row }) => (
        <Typography sx={{ p: '16px 16px 16px 0' }} variant="body1">
          {truncateString(
            isString(row.name) ? row.name : '',
            WORDS_PER_ROW_CELL,
          )}
        </Typography>
      ),
    },
    {
      field: 'uploaded_on',
      headerName: 'Uploaded On',
      flex: 1,
      renderCell: ({ row }) => (
        <Typography variant="body1">
          {new Date(row.uploaded_on).toLocaleDateString()}
        </Typography>
      ),
    },
    {
      field: 'url',
      headerName: 'Link',
      flex: 3,
      renderCell: ({ row }: GridRenderCellParams<FileRow>) => (
        <Link
          href={row.url}
          target="_blank"
          rel="noopener"
          onClick={() => handleLinkClick(row.url)}
        >
          {truncateString(row.url, WORDS_PER_ROW_CELL)}
        </Link>
      ),
    },
    {
      field: 'actions',
      headerName: 'Actions',
      flex: 2,
      renderCell: ({ row }) => (
        <Button
          id="revoke-file-access"
          variant="outlined"
          color={row.status === FILE_PICK_STATUS.REVOKED ? 'primary' : 'error'}
          size="small"
          onClick={() =>
            handleUpdateStatus(
              row.id,
              row.status === FILE_PICK_STATUS.REVOKED
                ? FILE_PICK_STATUS.ACTIVE
                : FILE_PICK_STATUS.REVOKED,
            )
          }
        >
          {row.status === FILE_PICK_STATUS.REVOKED
            ? 'Grant Access'
            : 'Revoke File Permission'}
        </Button>
      ),
    },
  ];

  const fetchFiles = async (type: 'self' | 'org') => {
    if (!userDetails) return;

    const paginationModel = type === 'self' ? paginationSelf : paginationOrg;
    const setLoading = type === 'self' ? setLoadingSelf : setLoadingOrg;
    const setRows = type === 'self' ? setRowsSelf : setRowsOrg;
    const setRowCount = type === 'self' ? setRowCountSelf : setRowCountOrg;

    setLoading(true);

    try {
      const { success, content } = await getFilePicks(
        paginationModel.page,
        paginationModel.pageSize,
        type,
      );

      if (!success || !content) {
        notify(UNHANDLED_ERROR, 'error');
        setRows([]);

        return;
      }

      const newRows = content.data.map((r) => ({
        id: r._id,
        file_id: r.file_id,
        name: r.name,
        url: r.url,
        uploaded_on: r.uploaded_on,
        status: r.status,
      }));

      setRows(newRows);
      setRowCount(content.totalCount);
    } catch {
      notify(UNHANDLED_ERROR, 'error');
      setRows([]);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    if (!userDetails) return;
    fetchFiles('self');
  }, [paginationSelf, userDetails]);

  useEffect(() => {
    if (!userDetails || !isAdmin) return;
    fetchFiles('org');
  }, [paginationOrg, userDetails]);

  const handleFileSelected = async (files: FileData[]) => {
    if (!files.length) return;
    notify(SYNC_FILE_PICK_STARTED, 'info');

    const file = files[0];
    const payload: FilePickPayload = {
      file_id: file.id,
      name: file.name,
      url: file.url,
    };

    const { success, content } = await createFilePick(payload);

    if (!success || !content) return;

    alertStore.notify(FILE_PICK_SUCCESS_MESSAGE, 'success');

    setRowsSelf((rs) => [
      {
        id: content._id,
        file_id: content.file_id,
        name: content.name,
        url: content.url,
        uploaded_on: content.uploaded_on,
        status: content.status,
      },
      ...rs,
    ]);
    setRowCountSelf((cnt) => cnt + 1);
    setPaginationSelf((m) => ({ ...m, page: 1 }));

    if (isAdmin) {
      fetchFiles('org');
    }
  };

  const handleUpdateStatus = async (
    chunkBatchId: string,
    status: FILE_PICK_STATUS,
  ) => {
    const { success } = await updateFilePickStatus({ chunkBatchId, status });

    if (!success) {
      notify(UNHANDLED_ERROR, 'error');

      return;
    }

    notify('File status updated successfully.', 'success');

    fetchFiles('self');

    if (isAdmin) {
      fetchFiles('org');
    }
  };

  const handleLinkClick = (url: string) => {
    navigator.clipboard.writeText(url);
    notify(COPY_NOTIFICATION_MESSAGE, 'info');
  };

  if (!userDetails) {
    return <Alert severity="error">{USER_DETAILS_NOT_FOUND_ERROR}</Alert>;
  }

  const filteredSelfRows = rowsSelf.filter((r) =>
    r.name.toLowerCase().includes(searchText.toLowerCase()),
  );

  const filteredOrgRows = rowsOrg.filter((r) =>
    r.name.toLowerCase().includes(searchText.toLowerCase()),
  );

  return (
    <Box component="main" sx={{ overflow: 'visible' }}>
      <ModuleHeader
        title="File Picker"
        toolTipProps={{
          clickId: 'file-picker',
          toolTipDescription: MODULE_USAGE_DESCRIPTION.FILE_PICKER,
        }}
        infoDrawerProps={{
          title: FILE_PICKER_MODULE_INFO_TITLE,
          markdownContent: FILE_PICKER_MODULE_INFO_MARKDOWN,
          ariaLabel: FILE_PICKER_MODULE_INFO_TITLE,
          iconButtonId: 'file_picker_module_info',
        }}
      />

      <SearchBar
        sx={{ mb: 2 }}
        onSearch={(q) => setSearchText(q)}
        onReset={() => setSearchText('')}
      />

      <Box
        sx={{
          display: 'flex',
          justifyContent: 'space-between',
          alignItems: 'center',
          mb: 1,
        }}
      >
        <Tabs value={tabValue} onChange={(_, val) => setTabValue(val)}>
          <Tab label="My Files" />
          <Tab label="Org Files" disabled={!isAdmin} />
        </Tabs>

        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
          <InfoDrawerTrigger
            title={FILE_PICKER_ACCESS_INFO_TITLE}
            markdownContent={FILE_PICKER_ACCESS_INFO_MARKDOWN}
            ariaLabel={FILE_PICKER_ACCESS_INFO_TITLE}
            iconButtonId="file_picker_access_info"
          />
          <GooglePickerComponent onFilesSelected={handleFileSelected} />
        </Box>
      </Box>

      {tabValue === 0 && (
        <DataGrid
          autoHeight
          getRowHeight={() => 'auto'}
          getRowId={(row) => row.id}
          pagination
          paginationMode="server"
          rows={filteredSelfRows}
          columns={columns.map((c) => ({ ...c, sortable: false }))}
          rowCount={rowCountSelf}
          loading={loadingSelf}
          pageSizeOptions={PAGE_SIZE_OPTIONS}
          initialState={{
            pagination: {
              paginationModel: {
                page: paginationSelf.page - 1,
                pageSize: paginationSelf.pageSize,
              },
            },
          }}
          onPaginationModelChange={({ page, pageSize }) => {
            setPaginationSelf({ page: page + 1, pageSize });
          }}
          slotProps={{
            pagination: { ActionsComponent: TablePaginationActions },
          }}
          disableColumnMenu
          disableRowSelectionOnClick
        />
      )}
      {tabValue === 1 &&
        (isAdmin ? (
          <DataGrid
            autoHeight
            getRowHeight={() => 'auto'}
            getRowId={(row) => row.id}
            pagination
            paginationMode="server"
            rows={filteredOrgRows}
            columns={columns.map((c) => ({ ...c, sortable: false }))}
            rowCount={rowCountOrg}
            loading={loadingOrg}
            pageSizeOptions={PAGE_SIZE_OPTIONS}
            initialState={{
              pagination: {
                paginationModel: {
                  page: paginationOrg.page - 1,
                  pageSize: paginationOrg.pageSize,
                },
              },
            }}
            onPaginationModelChange={({ page, pageSize }) => {
              setPaginationOrg({ page: page + 1, pageSize });
            }}
            slotProps={{
              pagination: { ActionsComponent: TablePaginationActions },
            }}
            disableColumnMenu
            disableRowSelectionOnClick
          />
        ) : (
          <Alert severity="info">
            You need admin privileges to view Org Files.
          </Alert>
        ))}
    </Box>
  );
});

/** Choose the isolated chat approval flow before mounting inventory hooks. */
const FilePicker = () => {
  const [params] = useSearchParams();

  return params.get('mode') === 'chat-access' ? (
    <DriveFileAccessApproval
      fileId={params.get('driveFileId')}
      autoPick={params.get('autoPick') === 'true'}
    />
  ) : (
    <FilePickerInventory />
  );
};

export default FilePicker;
